import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { isActivePath } from '@/app/nav';
import { MOBILE_QUERY, useMediaQuery } from '@/app/useMediaQuery';
import { useCurrentUser } from '@/auth/useSession';
import { hasNoDataOfItsOwn, useAccountState } from '@/features/focus';
import { useConnectPath, useSampleMode } from '@/features/sample/useSampleMode';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { TOUR_ID, TOUR_VERSION, shouldOffer, tourApi, tourKeys, toursQuery, type TourOutcome, type ToursResponse } from './api';
import { Coachmark } from './Coachmark';
import { TourContextValue, type TourScope } from './context';
import { TOUR_SCREENS, screenPath, stepsFor, type TourContext, type TourStep } from './steps';
import { WelcomeOffer } from './WelcomeOffer';

type Run = { scope: TourScope; steps: TourStep[]; index: number };

/**
 * The product tour, offered once per person (WelcomeOffer) and replayable at
 * any time from the sidebar or the command palette. It walks `TOUR_STEPS`
 * across screens, explains each region in whatever state it is in, and never
 * changes what the user is looking at beyond moving between screens. An
 * account with no data of its own takes it on sample data, ending on the way
 * to connect.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useCurrentUser();
  const connectPath = useConnectPath();
  const sample = useSampleMode();
  const { accountState } = useAccountState();
  const mobile = useMediaQuery(MOBILE_QUERY);
  const readOnly = Boolean(me.data?.support_session);
  const tours = useQuery({ ...toursQuery(), enabled: me.isSuccess && !readOnly });
  const [run, setRun] = useState<Run | null>(null);
  // Focus goes back where the tour was started from (the sidebar button), else to the screen.
  const opener = useRef<HTMLElement | null>(null);

  const save = useMutation({
    mutationFn: tourApi.save,
    onMutate: (outcome: TourOutcome) => {
      queryClient.setQueryData<ToursResponse>(tourKeys.tours(), (prev) => ({
        ...prev,
        [TOUR_ID]: { version: TOUR_VERSION, outcome, updated_at: new Date().toISOString() },
      }));
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<ToursResponse>(tourKeys.tours(), (prev) => ({ ...prev, [TOUR_ID]: saved }));
    },
  });
  const { mutateAsync } = save;
  // The answer is a remembered preference: failing to store it must not get in
  // the way. The offer stays answered for this visit either way.
  const record = useCallback(
    (outcome: TourOutcome): Promise<unknown> => (readOnly ? Promise.resolve() : mutateAsync(outcome).catch(() => undefined)),
    [readOnly, mutateAsync],
  );

  const ctx: TourContext = useMemo(() => ({ sample: sample.active, mobile }), [sample.active, mobile]);
  const fresh = !sample.active && hasNoDataOfItsOwn(accountState);

  const start = useCallback(
    (scope: TourScope) => {
      // A full tour of an account with nothing to show runs on the sample.
      const onSample = scope === 'all' && fresh;
      if (onSample) void sample.enter();
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setRun({ scope, steps: stepsFor(scope, { ...ctx, sample: ctx.sample || onSample }), index: 0 });
      track(ANALYTICS_EVENTS.tourStarted, { scope, sample: ctx.sample || onSample });
    },
    [ctx, fresh, sample],
  );

  const end = useCallback(
    (outcome: TourOutcome): Promise<unknown> => {
      if (!run) return Promise.resolve();
      const saved = run.scope === 'all' ? record(outcome) : Promise.resolve();
      track(outcome === 'completed' ? ANALYTICS_EVENTS.tourCompleted : ANALYTICS_EVENTS.tourDismissed, {
        scope: run.scope,
        step: run.steps[run.index]?.id ?? null,
      });
      setRun(null);
      requestAnimationFrame(() => {
        const target = opener.current?.isConnected ? opener.current : document.getElementById('main');
        target?.focus();
      });
      return saved;
    },
    [run, record],
  );

  const step = run ? run.steps[run.index] : undefined;

  // Each step is shown on its own screen; the Focus travels in the URL.
  useEffect(() => {
    if (!step?.screen) return;
    const path = screenPath(step.screen);
    if (!isActivePath(pathname, path)) void navigate({ to: path });
  }, [step, pathname, navigate]);

  const go = (delta: number) => {
    if (!run) return;
    const index = run.index + delta;
    if (index >= run.steps.length) void end('completed');
    else if (index >= 0) setRun({ ...run, index });
  };

  const screen = TOUR_SCREENS.find((s) => isActivePath(pathname, screenPath(s))) ?? null;
  const value = useMemo(() => ({ start, screen }), [start, screen]);
  const offered = !run && !readOnly && shouldOffer(tours.data);

  return (
    <TourContextValue.Provider value={value}>
      {children}
      {offered ? (
        <WelcomeOffer
          onSample={fresh}
          onStart={() => { start('all'); }}
          onDecline={() => {
            void record('dismissed');
            track(ANALYTICS_EVENTS.tourDismissed, { scope: 'all', step: null });
          }}
        />
      ) : null}
      {run && step ? (
        <Coachmark
          key={`${run.scope}:${step.id}`}
          step={step}
          index={run.index}
          total={run.steps.length}
          ctx={ctx}
          onBack={() => { go(-1); }}
          onNext={() => { go(1); }}
          onClose={() => { void end('dismissed'); }}
          onAction={() => {
            // Stored before the switch: leaving the sample refetches everything, the tours included.
            void end('completed').then(() => sample.exit(connectPath));
          }}
        />
      ) : null}
    </TourContextValue.Provider>
  );
}
